import { Autocomplete, Box, Checkbox, Grid, Stack, TextField, Typography } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import React, { useEffect, useMemo, useState } from 'react';
import { BackButton, Button, ErrorMessage, Heading, Input, Select, UploadImage } from '../../components/Common';
import { useLocation, useNavigate } from 'react-router-dom';
import { bloodGroupList, countryList } from '../../helper';
import { useMessage } from '../../components/Common/Message/MessageContext';
import { useGetCityListQuery, useGetStateListQuery } from '../../services/Location';
import { useAddNewAdminMutation, useUpdateAdminMutation, useGetAdminDetailQuery } from '../../services/Admin';
import { useGetPincodeByCityQuery, useGetExtraPincodeByCityQuery } from '../../services/SubAdmin';
//import pincodeData from '../Services/pincodeData.json';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AddAdmin = () => {
  const { showMessage } = useMessage();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [profileImage, setProfileImage] = useState([]);
  const [country, setCountry] = useState(1);
  const [states, setStates] = useState(null);
  const [city, setCity] = useState(null);
  const [selectedPincodes, setSelectedPincodes] = useState([]);
  const [aadharImage, setAadharImage] = useState([]);
  const [bloodGroup, setBloodGroup] = useState(null);
  const [formValues, setFormValues] = useState({
    name: '',
    mobile: '',
    email: '',
    guardianName: '',
    guardianMobile: '',
    address: '',
    panNo: '',
    dob: '',
  });
  const [errorMessages, setErrorMessages] = useState({});
  const [profileErrorMessage, setProfileErrorMessage] = useState('');
  const [addharErrorMessage, setAddharErrorMessage] = useState('');

  const { data: cityLst, error: cityError, isLoading: cityLoading } = useGetCityListQuery(states, { skip: !states });
  const cityList = cityLst?.data;
  const { data: stateLst, error: stateError, isLoading: stateLoading } = useGetStateListQuery();
  const stateList = stateLst?.data;

  // Currently edited admin ID (only available in edit mode)
  const editingAdminId = state?.id || null;

  // // Resolve selected city name from city id coming from dropdown
  // const selectedCityName = useMemo(() => {
  //   if (!city || !cityList) return null;
  //   const selected = cityList.find((c) => String(c.id) === String(city));
  //   return selected?.city || null;
  // }, [city, cityList]);

  // Fetch extra pincodes available for the city
  const { data: extraPincodeData, isLoading: extraPincodeLoading } = useGetExtraPincodeByCityQuery(city, {
    skip: !city,
    refetchOnMountOrArgChange: true,
  });

  // Fetch pincode ownership for the selected city
  const { data: cityPincodeOwnershipData, isLoading: cityPincodeLoading } = useGetPincodeByCityQuery(city, {
    skip: !city,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  // Map pincode -> adminId from backend ownership data
  const pincodeOwnershipMap = useMemo(() => {
    const map = {};
    const list = cityPincodeOwnershipData?.data || cityPincodeOwnershipData?.pinCodeData || [];
    list.forEach((item) => {
      const pin = item.pincode || item.pinCode;
      if (pin) {
        map[pin] = item.adminId || null;
      }
    });
    return map;
  }, [cityPincodeOwnershipData]);

  // All pincodes in selected city from extraPincode table, enriched with ownership info
  const cityPincodes = useMemo(() => {
    if (!city) return [];
    
    // Get extra pincodes from backend
    const extraPins = extraPincodeData?.data || [];
    
    // Combine with ownership data
    return extraPins.map((pin) => ({
      pincode: pin.pincode,
      adminId: pincodeOwnershipMap[pin.pincode] ?? null,
    }));
  }, [city, extraPincodeData, pincodeOwnershipMap]);

  const handleStateChange = (e) => setStates(e.target.value)
  const handleCountryChange = (e) => setCountry(e.target.value)
  const handleCityChange = (e) => setCity(e.target.value)
  const handleBloodGroupChange = (e) => setBloodGroup(e.target.value);

  const [addAdmin] = useAddNewAdminMutation();
  const [updateAdmin] = useUpdateAdminMutation();

  // Fetch admin detail when in edit mode - refetch on mount to get latest data
  const { data: adminDetailData } = useGetAdminDetailQuery(state?.id, {
    skip: !state?.id,
    refetchOnMountOrArgChange: true, // Always refetch when component mounts or ID changes
  });
  const adminPincodesFromBackend = useMemo(() => {
    // Debug: log the response structure
    if (adminDetailData && state?.id) {
      console.log('Admin Detail Response:', adminDetailData);
      console.log('Pincodes from backend:', adminDetailData?.data?.pincodes);
    }
    return adminDetailData?.data?.pincodes || [];
  }, [adminDetailData, state?.id]);

  // When city changes, preselect pincodes based on mode:
  // - Edit mode: DON'T preselect anything here, wait for admin detail to load
  // - Add mode: preselect only AVAILABLE pincodes for that city
  useEffect(() => {
    if (!city || !cityPincodes.length) {
      setSelectedPincodes([]);
      return;
    }

    // Only preselect in ADD mode (not edit mode)
    if (!state?.id) {
      // Add mode: preselect only pincodes that are NOT already assigned to any admin
      const availablePinsForCity = cityPincodes
        .filter((p) => !p.adminId) // only free pincodes
        .map((p) => p.pincode);
      setSelectedPincodes(availablePinsForCity);
    }
    // In edit mode, don't do anything here - let the admin detail effect handle it
  }, [city, cityPincodes, state?.id]);

  // When admin detail loads in edit mode, set only the admin's assigned pincodes
  useEffect(() => {
    // Only run in edit mode
    if (!state?.id) return;
    
    // Wait for both admin detail and city pincodes to be available
    if (city && cityPincodes.length > 0) {
      if (adminPincodesFromBackend.length > 0) {
        // Filter to only include pincodes that exist in the current city's pincode list
        const validPincodes = adminPincodesFromBackend.filter((pin) =>
          cityPincodes.some((cp) => cp.pincode === pin)
        );
        console.log('Setting pincodes from backend:', validPincodes);
        setSelectedPincodes(validPincodes);
      } else {
        // Admin detail loaded but no pincodes assigned, set empty array
        console.log('No pincodes assigned to this admin');
        setSelectedPincodes([]);
      }
    }
  }, [adminPincodesFromBackend, state?.id, city, cityPincodes]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const validateField = (field, fieldName) => {
    if (!field) {
      setErrorMessages(prevState => ({ ...prevState, [fieldName]: `${fieldName} is a mandatory field.` }));
      return true;
    }
    setErrorMessages(prevState => ({ ...prevState, [fieldName]: '' }));
    return false;
  };

  const handleFormSubmit = async () => {
    // Reset all error messages
    setErrorMessages({});
    setProfileErrorMessage('');
    setAddharErrorMessage('');
  
    // Validation for mandatory fields
    let hasError = false;
    
    // Check if profileImage is a File object or an array with a File
    const hasProfileImage = profileImage instanceof File || 
      (Array.isArray(profileImage) && profileImage.length > 0 && profileImage[0] instanceof File);
    
    const hasAadharImage = aadharImage instanceof File || 
      (Array.isArray(aadharImage) && aadharImage.length > 0 && aadharImage[0] instanceof File);
  
    if (!hasProfileImage && !state?.id) {
      setProfileErrorMessage('Please select a profile image to upload.');
      hasError = true;
    }
  
    if (!hasAadharImage && !state?.id) {
      setAddharErrorMessage('Please select an aadhar image to upload.');
      hasError = true;
    }
  
    // Validate fields
    const nameError = validateField(formValues.name, 'name');
    const mobileError = validateField(formValues.mobile, 'mobile');
    const emailError = validateField(formValues.email, 'email');
    const guardianNameError = validateField(formValues.guardianName, 'guardianName');
    const guardianMobileError = validateField(formValues.guardianMobile, 'guardianMobile');
    const countryError = validateField(country, 'country');
    const stateError = validateField(states, 'state');
    const cityError = validateField(city, 'city');
    const addressError = validateField(formValues.address, 'address');
    const panNoError = validateField(formValues.panNo, 'panNo');
    const dobError = validateField(formValues.dob, 'dob');
    const bloodGroupError = validateField(bloodGroup, 'bloodGroup');
  
    // If a city is selected and pincodes are available for that city, enforce at least one pincode selection
    const hasCityPincodes = city && cityPincodes.length > 0;
    let pincodeError = false;
    if (hasCityPincodes && selectedPincodes.length === 0) {
      setErrorMessages(prevState => ({ ...prevState, pincode: 'Please select at least one pincode for this city.' }));
      pincodeError = true;
    } else {
      setErrorMessages(prevState => ({ ...prevState, pincode: '' }));
    }
  
    // If any field has an error, prevent form submission
    if (
      nameError || mobileError || emailError || guardianNameError || guardianMobileError ||
      countryError || stateError || cityError || addressError || hasError ||
      panNoError || dobError || bloodGroupError || pincodeError
    ) {
      return;
    }
  
    const formData = new FormData();
    
    // Append profile image correctly
    if (profileImage instanceof File) {
      formData.append('profileImage', profileImage);
    } else if (Array.isArray(profileImage) && profileImage.length > 0 && profileImage[0] instanceof File) {
      formData.append('profileImage', profileImage[0]);
    }
    
    // Append aadhar image correctly
    if (aadharImage instanceof File) {
      formData.append('aadharImage', aadharImage);
    } else if (Array.isArray(aadharImage) && aadharImage.length > 0 && aadharImage[0] instanceof File) {
      formData.append('aadharImage', aadharImage[0]);
    }
  
    formData.append('bloodGroup', bloodGroup);
    formData.append('name', formValues.name);
    formData.append('mobile', formValues.mobile);
    formData.append('email', formValues.email);
    formData.append('guardianName', formValues.guardianName);
    formData.append('guardianMobile', formValues.guardianMobile);
    formData.append('country', country);
    formData.append('state', states);
    formData.append('city', city);
    formData.append('address', formValues.address);
    // Send selected pincodes as JSON array so backend can handle multiple
    formData.append('pincode', JSON.stringify(selectedPincodes));
    formData.append('panNo', formValues.panNo);
    formData.append('dob', formValues.dob);
  
    try {
      let response;
  
      if (state?.id) {
        response = await updateAdmin({ formData, id: state.id });
      } else {
        response = await addAdmin(formData);
      }
  
      console.log('Full Response:', response); // Debug log
  
      // RTK Query response structure: { data: { status, message }, error }
      if (response.error) {
        // Handle error response
        const errorMessage = response.error?.data?.message || 'Failed to save admin';
        showMessage('error', errorMessage);
        console.error('Error Response:', response.error);
      } else if (response.data) {
        // Handle success response
        const { status, message } = response.data;
        if (status) {
          showMessage('success', message || 'Admin saved successfully');
          navigate('/admin');
        } else {
          showMessage('error', message || 'Failed to save admin');
        }
      }
    } catch (error) {
      console.error('Failed to save the admin:', error);
      showMessage('error', 'An unexpected error occurred');
    }
  };

  useEffect(() => {
    if (state) {
      setFormValues({
        name: state?.data?.name,
        mobile: state?.data?.mobile,
        email: state?.data?.email,
        guardianName: state?.data?.guardianName,
        guardianMobile: state?.data?.guardianMobile,
        address: state?.data?.address,
        panNo: state?.data?.panNo,
        dob: new Date(state?.data?.dob).toISOString().split('T')[0],
        id: state.id,
      });
      setProfileImage(state?.data?.profileImage);
      setAadharImage(state?.data?.aadharImage);
      setBloodGroup(state?.data?.bloodGroup);
      setStates(state?.data?.state);
      setCity(state?.data?.city);
      // Clear pincodes initially in edit mode - they will be set when admin detail loads
      setSelectedPincodes([]);
      // setCountry(state?.data?.country);
    }
  }, [state]);

  return (
    <>
      <Stack flexDirection={'row'} gap={1} mb={1}>
        <BackButton />
        <Heading head='Add Admin' />
      </Stack>
      <Box className='card_container'>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>User Photo</Typography>
            <UploadImage files={profileImage} setFiles={setProfileImage} />
            <ErrorMessage message={profileErrorMessage} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Name' name="name" value={formValues.name} onChange={handleInputChange} />
            {errorMessages.name && <ErrorMessage message={errorMessages.name} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Mobile' type='number' name="mobile" value={formValues.mobile} onChange={handleInputChange} />
            {errorMessages.mobile && <ErrorMessage message={errorMessages.mobile} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Email' type='email' name="email" value={formValues.email} onChange={handleInputChange} />
            {errorMessages.email && <ErrorMessage message={errorMessages.email} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Guardian Name' name="guardianName" value={formValues.guardianName} onChange={handleInputChange} />
            {errorMessages.guardianName && <ErrorMessage message={errorMessages.guardianName} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='Guardian Mobile' name="guardianMobile" value={formValues.guardianMobile} onChange={handleInputChange} />
            {errorMessages.guardianMobile && <ErrorMessage message={errorMessages.guardianMobile} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Input labelinput='Pan Card No' name="panNo" value={formValues.panNo} onChange={handleInputChange} />
            {errorMessages.panNo && <ErrorMessage message={errorMessages.panNo} />}
          </Grid>
          <Grid item xs={12}>
            <Input labelinput='Address' name="address" value={formValues.address} onChange={handleInputChange} />
            {errorMessages.address && <ErrorMessage message={errorMessages.address} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select selectList={countryList} labelinput='Country' name="workType" value={country} onChange={handleCountryChange} />
            {errorMessages.country && <ErrorMessage message={errorMessages.country} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select selectList={stateLoading ? [] : stateList} labelinput='State' name="workType" value={states} onChange={handleStateChange} />
            {errorMessages.state && <ErrorMessage message={errorMessages.state} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <Select selectList={cityLoading ? [] : cityList} labelinput='City' name="workType" value={city} onChange={handleCityChange} />
            {errorMessages.city && <ErrorMessage message={errorMessages.city} />}
          </Grid>
          <Grid item xs={12}>
            <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>
              Select Pincodes
            </Typography>
            <Autocomplete
              multiple
              id="admin-pincode-select"
              options={cityPincodes}
              loading={extraPincodeLoading || cityPincodeLoading}
              disableCloseOnSelect
              getOptionLabel={(option) => option.pincode || ''}
              isOptionEqualToValue={(option, value) =>
                (option?.pincode || '') === (value?.pincode || '')
              }
              value={cityPincodes.filter((p) => selectedPincodes.includes(p.pincode))}
              onChange={(event, newValue) => {
                const pins = newValue.map((item) => item.pincode);
                setSelectedPincodes(pins);
                setErrorMessages(prev => ({ ...prev, pincode: '' }));
              }}
              // Disable pincodes that are already assigned to another admin
              getOptionDisabled={(option) => {
                // If no admin currently owns this pincode, it's always selectable
                if (!option.adminId) return false;
                // In edit mode, allow selecting pincodes that are already owned by this admin
                if (editingAdminId && option.adminId === editingAdminId) return false;
                // Otherwise, this pincode is owned by some other admin and must be disabled
                return true;
              }}
              renderOption={(props, option, { selected }) => {
                const isOwnedByOther =
                  !!option.adminId &&
                  (!editingAdminId || option.adminId !== editingAdminId);
              
                const { key, ...optionProps } = props;
              
                return (
                  <li
                    key={key}
                    {...optionProps}
                    style={{
                      backgroundColor: isOwnedByOther ? '#e3f2fd' : '#ffffff',
                      opacity: isOwnedByOther ? 0.7 : 1,
                    }}
                  >
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                      disabled={isOwnedByOther}
                    />
              
                    <span>{option.pincode}</span>
              
                    {isOwnedByOther && (
                      <Typography
                        component="span"
                        fontSize="11px"
                        color="text.secondary"
                        ml={1}
                      >
                        (Already assigned)
                      </Typography>
                    )}
                  </li>
                );
              }}
              
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search & select pincodes"
                />
              )}
            />
            {errorMessages.pincode && <ErrorMessage message={errorMessages.pincode} />}
          </Grid>
          <Grid item xs={12}>
            <Typography fontSize={{ xs: '12px', sm: '13px' }} fontWeight={500} mb={'2px'}>Upload Aadhar Card</Typography>
            <UploadImage files={aadharImage} setFiles={setAadharImage} />
            <ErrorMessage message={addharErrorMessage} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Select selectList={bloodGroupList} labelinput='Blood Group' name="bloodGroup" value={bloodGroup} onChange={handleBloodGroupChange} />
            {errorMessages.bloodGroup && <ErrorMessage message={errorMessages.bloodGroup} />}
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Input labelinput='User DOB' name="dob" type='date' value={formValues.dob} onChange={handleInputChange} />
            {errorMessages.dob && <ErrorMessage message={errorMessages.dob} />}
          </Grid>
          <Grid item xs={12}>
            <Stack flexDirection={'row'} gap={1}>
              <Button buttonName='save' onClick={handleFormSubmit} />
              <Button buttonName='cancel' color='white' onClick={() => navigate('/admin')} />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default AddAdmin;
